import { readFeEnv } from './feEnv';

/**
 * Split-host layouts (SPA origin → API origin + v2 prefix).
 * Used when Railway/CRA build omits REACT_APP_* (still common on frontend-only deploys).
 */
const SPLIT_HOST_LAYOUTS = {
  'tunewaves.com': { apiBase: 'https://tyly.tunewaves.com', apiPrefix: '/api/v2' },
  'www.tunewaves.com': { apiBase: 'https://tyly.tunewaves.com', apiPrefix: '/api/v2' },
  'v2.tunewaves.com': { apiBase: 'https://poligon.tunewaves.com', apiPrefix: '/api/v2' },
};

function splitHostLayout() {
  if (typeof window === 'undefined') {
    return null;
  }
  return SPLIT_HOST_LAYOUTS[window.location.hostname] || null;
}

/**
 * API origin for fetch(). Evaluated at **runtime** so production builds served on
 * localhost still use same-origin `/api` (Vite/CRA dev proxy). Build-time
 * `REACT_APP_API_URL` wins when set (e.g. prod split-host → tyly).
 */
export function getApiBaseUrl() {
  const fromEnv = readFeEnv('REACT_APP_API_URL').trim();
  if (fromEnv.length > 0) {
    return fromEnv;
  }

  const viteDev =
    typeof import.meta !== 'undefined' && import.meta.env?.DEV;
  const nodeDev =
    typeof process !== 'undefined' && process.env.NODE_ENV === 'development';
  if (viteDev || nodeDev) {
    return '';
  }

  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    if (host === 'localhost' || host === '127.0.0.1') {
      return '';
    }
    const layout = splitHostLayout();
    if (layout?.apiBase) {
      return layout.apiBase;
    }
  }

  return '';
}

/** REST path prefix (`/api` legacy, `/api/v2` after cutover). */
export function getApiPrefix() {
  const fromEnv = readFeEnv('REACT_APP_API_PREFIX', '').trim();
  if (fromEnv.length > 0) {
    return fromEnv.replace(/\/$/, '') || '/api';
  }
  const layout = splitHostLayout();
  if (layout?.apiPrefix) {
    return layout.apiPrefix;
  }
  return '/api';
}

/**
 * Map paths that start with `/api` to {@link getApiPrefix} (e.g. `/api/accounts/` → `/api/v2/accounts/`).
 * @param {string} endpoint
 */
export function normalizeApiEndpoint(endpoint) {
  if (typeof endpoint !== 'string') {
    return endpoint;
  }
  const prefix = getApiPrefix();
  if (endpoint === prefix || endpoint.startsWith(`${prefix}/`)) {
    return endpoint;
  }
  if (endpoint === '/api' || endpoint.startsWith('/api/')) {
    return endpoint.replace(/^\/api(?=\/|$)/, prefix);
  }
  return endpoint;
}

export default {
  get API_BASE_URL() {
    return getApiBaseUrl();
  },
  get API_PREFIX() {
    return getApiPrefix();
  },
  normalizeApiEndpoint,
};
